import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Text } from '../../../../components';
import { DNS_TYPE_COLORS, DNS_TYPE_BG } from '../../../../theme/tokens';
import type { CasinoDomain } from '../../../../mock-data/types';
import { Sheet } from '@/nova/components/Sheet';
import {
  Subtitle,
  List,
  Record,
  RecordTop,
  TypeBadge,
  RecordName,
  Cols,
  Col,
  ColLabel,
  ColRow,
  ColValue,
  CopyBtn,
} from './DnsModal.styles';

export type DnsModalProps = {
  domain: CasinoDomain;
  onClose: () => void;
  /** Page-level toast (copy confirmation + interpolation). */
  onToast: (message: string, placeholders?: Record<string, string | number>) => void;
};

/**
 * DNS settings overlay for an unmanaged domain — the records the owner must set at
 * their registrar to keep the domain pointed at the Whitelabels.com site. Same card
 * layout + per-field copy as Settings → Website Domain.
 */
export const DnsModal = ({ domain, onClose, onToast }: DnsModalProps): React.ReactElement => {
  const { t } = useTranslation();
  const records = domain.dns ?? [];

  const copyField = (value: string, message: string) => (): void => {
    void navigator.clipboard
      ?.writeText(value)
      .then(() => onToast(message))
      .catch(() => onToast(t("Couldn't copy to clipboard")));
  };

  return (
    <Sheet
      title={t('DNS settings')}
      ariaLabel={t('DNS settings for {{domain}}')}
      ariaLabelPlaceholders={{ domain: domain.name }}
      onClose={onClose}
      footer={<Button label={t('Done')} variant="primary" onClick={onClose} />}
    >
      <Subtitle
        text="Add these records at {{registrar}} so {{domain}} stays pointed at your Whitelabels.com site."
        placeholders={{ registrar: domain.registrar, domain: domain.name }}
        tag="span"
      />
      <List>
        {records.map((record, index) => {
          const fields = [
            { label: t('Host'), value: record.host, aria: t('Copy host'), message: t('Host copied to clipboard') },
            { label: t('Value'), value: record.value, aria: t('Copy value'), message: t('Value copied to clipboard') },
            { label: t('TTL (Seconds)'), value: String(record.ttl), aria: t('Copy TTL'), message: t('TTL copied to clipboard') },
          ];
          return (
            <Record key={`${record.type}-${record.host}-${index}`}>
              <RecordTop>
                <TypeBadge $color={DNS_TYPE_COLORS[record.type]} $bg={DNS_TYPE_BG[record.type]}>
                  <Text text={record.type.charAt(0)} tag="span" />
                </TypeBadge>
                <RecordName text="{{type}} Record" placeholders={{ type: record.type }} tag="span" />
              </RecordTop>
              <Cols>
                {fields.map((field) => (
                  <Col key={field.label}>
                    <ColLabel text={field.label} tag="span" />
                    <ColRow>
                      <ColValue text={field.value} tag="span" title={field.value} />
                      <CopyBtn type="button" aria-label={field.aria} onClick={copyField(field.value, field.message)}>
                        <Icon name="copy" size={14} />
                      </CopyBtn>
                    </ColRow>
                  </Col>
                ))}
              </Cols>
            </Record>
          );
        })}
      </List>
    </Sheet>
  );
};

export default DnsModal;
